import { makeAutoObservable, observableRef } from 'mobx';
import { token } from '@/infrastructure/container';
import type { IAccessGateway, IAdminGateway } from '@/domain/accessGateway';
import { ServerError } from '@/domain/errors';
import { emptyProjectState, type ProjectEvent, type ProjectState } from '@/domain/project';
import type { SessionEvent } from '@/domain/session';
import type { ICeleriantClient, ProjectHandle } from './CeleriantClient';
import type { IClock } from './Clock';
import { GatewayReach } from './GatewayReach';
import type { ILogger } from './Logger';
import type { ISessionConnection } from './SessionGateway';
import { translate } from './translate';

// The project aggregate's side of the SDK boundary: the day's run list and the share-link registry
// (docs/sync.md). Same shape as SessionGateway — one handle, one fold as one `observableRef`, every
// SDK throw translated on the way out.
//
// ONE class behind BOTH access facades, registered once and aliased to two tokens, exactly as
// SessionGateway is. Two implementations would open two project handles and split the registry
// every screen reads. The facades stay two because of what they let a caller DO: a device that
// scanned a QR is handed `IAccessGateway` and cannot see a mint method at all, and minting needs
// Owner on the aggregate, which only the admin laptop has (docs/sync.md).
//
// Opening the SESSION is delegated to SessionGateway rather than done here, for the same reason:
// one session aggregate, one fold, one handle.

type Unsubscribe = () => void;

// The SDK's `execute` correlates kind and payload through a generic. Our events carry the same
// correlation in a discriminated union, and TypeScript cannot see that the two are the same fact.
// This is that seam, and it is the only cast in the file: both unions are written against the same
// aggregate definitions in src/domain/aggregates.ts that type the handle.
type AnyExecute = (
  kind: string,
  payload: unknown,
  opts: { idempotencyKey: string },
) => Promise<unknown>;

// The mint options the SDK's `handle.share` takes, typed structurally so this file keeps no SDK
// edge (INVARIANTS rule 22).
const CONTRIBUTOR_MULTI_USE = { accessLevel: 'contributor', singleUse: false } as const;
// Viewer, not contributor: the project key exists so a redeeming phone can READ the registry and
// learn what it became. Handing the hall write access to the run list buys nothing.
const VIEWER_MULTI_USE = { accessLevel: 'viewer', singleUse: false } as const;

export const TOKEN_ProjectGateway = token<ProjectGateway>('ProjectGateway');

export class ProjectGateway implements IAccessGateway, IAdminGateway {
  private _state: ProjectState = emptyProjectState();
  private _handle: ProjectHandle | undefined;
  private _unsubscribe: Unsubscribe | undefined;
  private _unsubscribeStatus: Unsubscribe | undefined;
  private _projectId: string | undefined;
  private readonly reach: GatewayReach;

  constructor(
    private readonly celeriant: ICeleriantClient,
    private readonly sessions: ISessionConnection,
    clock: IClock,
    private readonly logger: ILogger,
  ) {
    this.reach = new GatewayReach(clock, logger, () => this.probe());
    makeAutoObservable<
      ProjectGateway,
      '_state' | '_handle' | '_unsubscribe' | '_unsubscribeStatus' | 'reach'
    >(this, {
      _state: observableRef,
      _handle: observableRef,
      _unsubscribe: false,
      _unsubscribeStatus: false,
      reach: false,
    });
  }

  // The registry and the run list, as a MobX observable. Valid after openProject; before it, the
  // empty fold a device that has not subscribed yet genuinely has.
  get projectState(): ProjectState {
    return this._state;
  }

  get projectId(): string | undefined {
    return this._projectId;
  }

  get gatewayReachable(): boolean {
    return this.reach.reachable;
  }

  // --- IAccessGateway ---------------------------------------------------------------------------

  redeemProjectShare = async (projectId: string, shareKey: string): Promise<void> => {
    await this.call('redeemProjectShare', () =>
      this.celeriant.redeemProjectShare(projectId, shareKey),
    );
  };

  redeemSessionShare = async (sessionId: string, shareKey: string): Promise<void> => {
    await this.call('redeemSessionShare', () =>
      this.celeriant.redeemSessionShare(sessionId, shareKey),
    );
  };

  openProject = async (projectId: string): Promise<void> => {
    const open = this._handle;
    if (this._projectId === projectId && open !== undefined) {
      // Already open, so there is no handle to swap — but do NOT just return. Every join answers
      // itself out of this fold the moment openProject resolves, and a device that has held this
      // handle since 9am may never have been told about the code the admin revoked at 11am: a
      // slept phone, a dropped SSE, a hall wifi hiccup. Returning early admits a revoked code on
      // the strength of a fold nothing refreshed. One re-read per join is the cost of the answer
      // being current.
      //
      // Through `call`, deliberately NOT through firstCatchUp: firstCatchUp CLOSES the handle when
      // the read fails, which is right for an open that has nothing to lose and wrong here, where
      // one flaky read would tear down a live join that is working. A failure here throws a
      // translated error and leaves the handle attached.
      await this.call('refreshProject', () => open.refresh());
      return;
    }
    const handle = await this.call('openProject', () => this.celeriant.projectHandle(projectId));
    // Tear the old one down first, for the reason SessionGateway does: an unsubscribed listener
    // cannot write a dead project's fold over a live one.
    await this.detach();
    // AWAIT the first catch-up. The SDK's open() fires it fire-and-forget, so `aggregateOnline`
    // resolves with an empty projection even for a project full of events. This is the read the
    // whole join depends on: OnboardingStore looks its code hash up in this fold the moment
    // openProject resolves, and an empty registry tells a perfectly valid code it is not valid.
    await this.firstCatchUp(handle);
    const unsubscribe = handle.subscribe((state) => this.applyState(state));
    const unsubscribeStatus = handle.subscribeStatus((status) => this.reach.apply(status));
    this.attach(projectId, handle, unsubscribe, unsubscribeStatus);
    this.reach.apply(handle.status);
  };

  openSession = async (sessionId: string): Promise<void> => {
    await this.call('openSession', () => this.sessions.open(sessionId));
  };

  // --- IAdminGateway ----------------------------------------------------------------------------

  // ONE write attempt. The retry ladder lives in AdminAccessStore, which holds the idempotency key
  // across attempts.
  executeProject = async (event: ProjectEvent, opts: { idempotencyKey: string }): Promise<void> => {
    const handle = this._handle;
    if (handle === undefined) throw new ServerError('admin: no project is open');
    const execute = handle.execute as AnyExecute;
    await this.call(event.kind, () =>
      execute(event.kind, event.payload, { idempotencyKey: opts.idempotencyKey }),
    );
  };

  // Writes on a session the admin device owns, including the CREATING write on a fresh one. The
  // first execute() creates the aggregate and makes this device its owner, which is what makes it
  // the only device that can mint the session's codes (docs/sync.md).
  executeSession = async (
    sessionId: string,
    event: SessionEvent,
    opts: { idempotencyKey: string },
  ): Promise<void> => {
    const handle = await this.call('sessionHandle', () => this.celeriant.sessionHandle(sessionId));
    const execute = handle.execute as AnyExecute;
    await this.call(event.kind, () =>
      execute(event.kind, event.payload, { idempotencyKey: opts.idempotencyKey }),
    );
  };

  // Contributor and multi-use: three coaches scan the same court code, and minting it single-use
  // breaks the second and third coach (docs/sync.md).
  mintSessionShare = async (sessionId: string): Promise<{ shareKey: string }> => {
    const handle = await this.call('sessionHandle', () => this.celeriant.sessionHandle(sessionId));
    return this.call('mintSessionShare', () => handle.share(CONTRIBUTOR_MULTI_USE));
  };

  mintProjectViewerShare = async (): Promise<{ shareKey: string }> => {
    const handle = this._handle;
    if (handle === undefined) throw new ServerError('admin: no project is open');
    return this.call('mintProjectViewerShare', () => handle.share(VIEWER_MULTI_USE));
  };

  close = async (): Promise<void> => {
    await this.detach();
  };

  // Every SDK call in this file goes through here, so there is exactly one place an SDK error class
  // can escape from and exactly one place it is translated (INVARIANTS rule 17).
  private call = async <T>(what: string, run: () => Promise<T>): Promise<T> => {
    try {
      return await run();
    } catch (cause) {
      const failure = translate(cause, what);
      this.logger.warn(`project gateway: ${what} failed: ${failure.message}`);
      throw failure;
    }
  };

  // Actions. makeAutoObservable has wrapped every method, so the SDK subscription is batched.
  private applyState(state: ProjectState): void {
    this._state = state;
  }

  private probe = async (): Promise<void> => {
    await this._handle?.refresh();
  };

  // Closes the handle it was handed if the catch-up fails, so a rejected open leaves nothing
  // half-attached and nothing leaked in the SDK's handle cache.
  private async firstCatchUp(handle: ProjectHandle): Promise<void> {
    try {
      await handle.refresh();
    } catch (cause) {
      await handle.close();
      throw translate(cause, 'open project');
    }
  }

  private attach(
    projectId: string,
    handle: ProjectHandle,
    unsubscribe: Unsubscribe,
    unsubscribeStatus: Unsubscribe,
  ): void {
    this._handle = handle;
    this._unsubscribe = unsubscribe;
    this._unsubscribeStatus = unsubscribeStatus;
    this._projectId = projectId;
    this._state = handle.state;
  }

  private async detach(): Promise<void> {
    const handle = this._handle;
    this._unsubscribe?.();
    this._unsubscribeStatus?.();
    this._unsubscribe = undefined;
    this._unsubscribeStatus = undefined;
    this._handle = undefined;
    this._projectId = undefined;
    this._state = emptyProjectState();
    this.reach.reset();
    await handle?.close();
  }
}
